import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import { useCoreData, reprocessLea } from '@/queries.ts'
import { fetchAuthenticatedData } from '@/queries/shared.ts'
import { CoreDataModel, GlobalStateModel, LeaDataModel, LeaModel, LeaTestDataModel, PreschoolModel, RecordType } from '@/models.ts'
import { chldExportCSV, clrmExportCSV, stffExportCSV } from '@/models/csv-data'
import { newClassrooms, newStaff, newStaffAssignments, newFamilies, newChildren, newEnrollments, newStaffClassrooms, newChildClassrooms, newAttendances } from '@/dev/make-data'

const rootRoute = getRouteApi('__root__')

export async function getAllLeaData(globalState: GlobalStateModel, leaid: string): Promise<LeaTestDataModel> {
    var leaData: LeaTestDataModel = {}
    await Promise.all([
        fetchAuthenticatedData<LeaModel>(globalState, `/json/entity/${leaid}`).then(data => leaData.lea = data),
        globalState.queryClient.ensureQueryData({
            queryKey: ['lea', leaid],
            queryFn: () => fetchAuthenticatedData<LeaModel>(globalState, `/json/entity/${leaid}/data`),
        }).then(data => {
            leaData = { ...leaData, ...data }
        }),
        fetchAuthenticatedData<PreschoolModel[]>(globalState, `/json/entity/${leaid}/preschools`).then(data => leaData.preschools = data)
    ])
    return Promise.resolve(leaData)
};

export function useMutationMakeLocalData() {
    const queryClient = useQueryClient()
    const globalState = rootRoute.useLoaderData()
    const core = useCoreData()
    const mutation = useMutation({
        mutationFn: (leaId: string) => {
            const leaData = getAllLeaData(globalState, leaId)
            return leaData.then(lea_data => {
                makeLocalLeaData(core, lea_data)
                reprocessLea(globalState.hasRole, lea_data)
                return lea_data
            })
        },
        onSuccess: (data, leaId) => {
            queryClient.setQueryData(['lea', leaId], data)
        },
        onError: (error) => {
            console.error(error)
        },
    })
    return mutation
}

export const makeDataFns: Record<RecordType, (core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number, percent_new: number) => void> = {
    CHLD: addNewChildren,
    FAMI: addNewFamilies,
    CLEN: addNewChildClassrooms,
    //LANG: addNewLanguages,
    //SLNG: 'Supplemental DLL (SLNG)',
    STFF: addNewStaff,
    SASS: addNewStaffAssignments,
    CLRM: addNewClassrooms,
    SCLR: addNewStaffClassrooms,
    ENRL: addNewEnrollments,
    ATTN: addNewAttendances,
}

function addNewClassrooms(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number, percent_new: number) {
  lea_data.clrm = [...lea_data.clrm, ...newClassrooms(core_data, lea_data, num_records, percent_new)]
  clrmExportCSV(lea_data.clrm)
}
function addNewStaff(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number, percent_new: number) {
  lea_data.stff = [...lea_data.stff, ...newStaff(core_data, lea_data, num_records, percent_new)]
  stffExportCSV(lea_data.stff)
}
function addNewStaffAssignments(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number) {
    lea_data.sass = [...lea_data.sass, ...newStaffAssignments(core_data, lea_data, num_records)]
}
function addNewStaffClassrooms(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number) {
    lea_data.sclr = [...lea_data.sclr, ...newStaffClassrooms(core_data, lea_data, num_records)]
}
function addNewFamilies(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number, percent_new: number) {
    lea_data.fami = [...lea_data.fami, ...newFamilies(core_data, lea_data, num_records, percent_new)]
}
function addNewChildren(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number, percent_new: number) {
  lea_data.chld = [...lea_data.chld, ...newChildren(core_data, lea_data, num_records, percent_new)]
  chldExportCSV(lea_data.chld)
}
function addNewEnrollments(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number) {
    lea_data.enrl = [...lea_data.enrl, ...newEnrollments(core_data, lea_data, num_records)]
}
function addNewChildClassrooms(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number) {
    lea_data.clen = [...lea_data.clen, ...newChildClassrooms(core_data, lea_data, num_records)]
}
function addNewAttendances(core_data: CoreDataModel, lea_data: LeaTestDataModel, num_records: number) {
    lea_data.attn = [...lea_data.attn, ...newAttendances(core_data, lea_data, num_records)]
}

function makeLocalLeaData(core_data: CoreDataModel, lea_data: LeaTestDataModel) {
    lea_data.clrm = newClassrooms(core_data, lea_data, 3)
    lea_data.stff = newStaff(core_data, lea_data, 10)
    lea_data.sass = newStaffAssignments(core_data, lea_data, 5)
    lea_data.sclr = newStaffClassrooms(core_data, lea_data, 15)
    lea_data.fami = newFamilies(core_data, lea_data, 1700, 50)
    lea_data.chld = newChildren(core_data, lea_data, 2000, 60)
    lea_data.enrl = newEnrollments(core_data, lea_data, 40)
    lea_data.clen = newChildClassrooms(core_data, lea_data, 10)
    lea_data.attn = newAttendances(core_data, lea_data, 600)

    //    lea_data.clrm = newClassrooms(core_data, lea_data, 100)
//    lea_data.staff = newStaff(core_data, lea_data, 200)
//    lea_data.sass = newStaffAssignments(core_data, lea_data, 5)
//    lea_data.sclr = newStaffClassrooms(core_data, lea_data, 250)
//    lea_data.fami = newFamilies(core_data, lea_data, 1700, 5000)
//    lea_data.chld = newChildren(core_data, lea_data, 2000, 6000)
//    lea_data.enrl = newEnrollments(core_data, lea_data, 400)
//    lea_data.clen = newChildClassrooms(core_data, lea_data, 100)
//    lea_data.attn = newAttendances(core_data, lea_data, 6000)
}
